import React, { Fragment } from 'react'
import { useState, useEffect, useLifecycle } from 'sb-hooks'


interface ComboBoxChildren {
  id: string
  inputId: string,
  controlsId: string
  descendantId: string
  activeDescendantId?: string
  isOpen: boolean
  setIsOpen(value: boolean): void
}

interface ComboBoxProps {
  children(ComboBoxChildren): React.ReactNode,
  name: string

  // TODO: temporary measures, remove after search input will be reactored
  uid: string
  onAfterClose(): void
}

const getUniqueId = ((id) => () => String(++id))(1)

const ComboBox: React.FunctionComponent<ComboBoxProps> = ({ children, name, uid: _uid, onAfterClose }) => {
  const [ isOpen, setIsOpen ] = useState(false)
  const [ activeDescendantId, setActiveDescendantId ] = useState(null)

  const uid          = _uid || getUniqueId()
  const id           = `${name}_${uid}`
  const inputId      = `${name}_input_${uid}`
  const controlsId   = `${name}_controls_${uid}`
  const descendantId = `${name}_descendant_${uid}`

  useLifecycle((isFirstRender) => {
    if (!isFirstRender && !isOpen && typeof onAfterClose === 'function') {
      onAfterClose()
    }
  }, [ isOpen, onAfterClose ])

  useEffect(() => {
    const focusOn = (element: HTMLElement): void => {
      if (element) {
        element.focus()

        if ((element.id || '').includes(descendantId)) {
          setActiveDescendantId(element.id)
        }
      }
    }

    const listener = (event) => {
      if (event.key === 'Escape') {
        setActiveDescendantId(null)
        setIsOpen(false)

        return event.preventDefault()
      }

      const isDescendant = (event.target && event.target.id || '').includes(descendantId)
      const descendants: HTMLElement[] = Array.from(document.querySelectorAll(`[id*=${descendantId}]`))

      if (event.key === 'ArrowDown') {
        const first = descendants[0]

        if (isDescendant) {
          const next = descendants[descendants.indexOf(event.target) + 1]

          if (next) {
            focusOn(next)
          }
          else {
            focusOn(first)
          }
        }
        else {
          focusOn(first)
        }

        return event.preventDefault()
      }
      if (event.key === 'ArrowUp') {
        const last = descendants[descendants.length - 1]

        if (isDescendant) {
          const prev = descendants[descendants.indexOf(event.target) - 1]

          if (prev) {
            focusOn(prev)
          }
          else {
            focusOn(last)
          }
        }
        else {
          focusOn(last)
        }

        return event.preventDefault()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', listener)
    }

    return () => {
      setActiveDescendantId(null)
      document.removeEventListener('keydown', listener)
    }
  }, [ isOpen, id, descendantId ])

  if (typeof children !== 'function') {
    console.warn('ComboBox: children should be passed as a function')
    return null
  }

  return (
    <Fragment>
      {
        children({
          id,
          inputId,
          controlsId,
          descendantId,
          activeDescendantId,
          isOpen,
          setIsOpen,
        })
      }
    </Fragment>
  )
}


export default ComboBox
